import {formatDateTimeWith, formatIsoMinuteWithZone, parseApiInstant} from "core/format/dateFormat";
import type {Locale} from "i18n/localePreference";

/**
 * The § 312k BGB cancellation form's rules (kitecorp/transferify-ui#2327). React-free by mandate (epic #26).
 *
 * <p>§ 312k Abs. 2 S. 3 Nr. 1 fixes the field set: the page must invite and allow the KIND of termination
 * (and, for an extraordinary one, its reason), the consumer's clear identification, a clear identification
 * of the contract, the date the termination should take effect, and a way to deliver the confirmation
 * electronically. That is why `email` and `kind` are the only required answers and everything else is
 * optional: LG Köln 33 O 355/22 reads that list as a MAXIMUM as well as a minimum, so a field we add
 * beyond it is a risk, and a required field beyond it is a bigger one.
 *
 * <p><strong>Nothing here may become a retention mechanic.</strong> BGH I ZR 200/25 (FitX, 16.07.2026) holds
 * that the confirmation page carries only the statutory content. No offer, no survey, no "are you sure",
 * and no password prompt — a password prompt is what LG Köln struck down.
 *
 * <p>Errors are returned as KEYS of the catalogue's `cancellation.errors` namespace, never as copy: a rule
 * that returned English would have to be rewritten to add a language.
 */

/** The two kinds § 312k Abs. 2 names, plus the unanswered state a page with no preselection starts in. */
export type ContractCancellationKind = "ORDINARY" | "EXTRAORDINARY";

/** What the form holds. Every field is a string because every field is an input's value. */
export interface ContractCancellationDraft {
	email: string;
	name: string;
	contractDetail: string;
	/** Empty until the reader chooses. Nothing is preselected — see {@link emptyCancellationDraft}. */
	kind: ContractCancellationKind | "";
	reason: string;
	/** ISO `yyyy-MM-dd`, which is what a native date input produces. Empty means Abs. 5's default. */
	requestedEffectiveDate: string;
}

export type ContractCancellationField = keyof ContractCancellationDraft;

/**
 * Field bounds, the same numbers the api's `ContractCancellationRequest` enforces.
 *
 * <p>They exist here so the reader is told WHICH field is too long instead of meeting one generic server
 * error — a 400 from this endpoint reads as "you typed something wrong" when the real cause is the two sides
 * disagreeing, which is not a sentence anybody can act on.
 *
 * <p>A LOOSER bound here is therefore the defect to guard against, and it is not hypothetical: the first
 * draft of this file had `name: 200` and `contractDetail: 500` against the api's 80 and 200, so a reader
 * with a long contract reference would have been let through and then refused by the server.
 * `contractCancellationLimits.contract.test.ts` reads the api's own constants and fails on any drift, in
 * either direction, rather than trusting two hand-copied literals to stay in step.
 *
 * <p>`email` is the SMTP mailbox maximum (RFC 5321), which is what the api's boundary uses
 * (`@Size(max = MailboxGrammar.MAXIMUM_LENGTH)`) — deliberately tighter than its own 320-character column,
 * because the confirmation has to be deliverable to this address.
 */
export const CANCELLATION_LIMITS = {
	email: 254,
	name: 80,
	contractDetail: 200,
	reason: 2000,
} as const;

/**
 * A validation failure per field, valued by a key of `cancellation.errors`.
 *
 * `tooLong` is shared by every bounded field on purpose: the reader needs to know which field, which the
 * KEY of this record already says, not a different sentence per field.
 */
export type ContractCancellationErrorKey =
	| "emailRequired"
	| "emailInvalid"
	| "kindRequired"
	| "reasonRequired"
	| "dateInvalid"
	| "tooLong";

export type ContractCancellationErrors = Partial<Record<ContractCancellationField, ContractCancellationErrorKey>>;

/**
 * The starting draft. `kind` is EMPTY, and that is the requirement rather than an oversight: Abs. 2 S. 3 Nr. 1
 * lit. a asks the page to invite the consumer to state the kind, and a preselected radio states it for them.
 */
export function emptyCancellationDraft(): ContractCancellationDraft {
	return {email: "", name: "", contractDetail: "", kind: "", reason: "", requestedEffectiveDate: ""};
}

/**
 * A deliberately conservative deliverability shape: a single `@`, something before it with no whitespace,
 * and a dotted domain after it.
 *
 * The api is the authority (`@DeliverableMailbox`), and it refuses `user@localhost` because the SMTP
 * transport later would — which is why the domain here must carry a dot. This check only exists so the
 * reader hears about a typo before paying for a round trip.
 */
const DELIVERABLE_MAILBOX = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

/** An ISO calendar date that actually exists, so 2026-02-30 is refused rather than rolled into March. */
function isRealIsoDate(value: string): boolean {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
		return false;
	}
	const parsed = new Date(`${value}T00:00:00Z`);
	return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(value);
}

/** Every independent problem with the draft at once, so one submit does not surface them one at a time. */
export function validateContractCancellation(draft: ContractCancellationDraft): ContractCancellationErrors {
	const errors: ContractCancellationErrors = {};

	const email = draft.email.trim();
	if (email.length === 0) {
		errors.email = "emailRequired";
	}
	else if (email.length > CANCELLATION_LIMITS.email) {
		errors.email = "tooLong";
	}
	else if (!DELIVERABLE_MAILBOX.test(email)) {
		errors.email = "emailInvalid";
	}

	if (draft.kind === "") {
		errors.kind = "kindRequired";
	}

	if (draft.name.trim().length > CANCELLATION_LIMITS.name) {
		errors.name = "tooLong";
	}
	if (draft.contractDetail.trim().length > CANCELLATION_LIMITS.contractDetail) {
		errors.contractDetail = "tooLong";
	}

	// Required for an extraordinary cancellation and meaningless for an ordinary one, so an ordinary draft
	// is never held to it — and a leftover reason on an ordinary draft is dropped by `requestBodyOf`
	// rather than reported, because the reader did not ask for it to be sent.
	const reason = draft.reason.trim();
	if (draft.kind === "EXTRAORDINARY" && reason.length === 0) {
		errors.reason = "reasonRequired";
	}
	else if (reason.length > CANCELLATION_LIMITS.reason) {
		errors.reason = "tooLong";
	}

	// Abs. 5 makes an ABSENT date the documented default ("im Zweifel zum frühestmöglichen Zeitpunkt"), so
	// empty is never an error here.
	if (draft.requestedEffectiveDate !== "" && !isRealIsoDate(draft.requestedEffectiveDate)) {
		errors.requestedEffectiveDate = "dateInvalid";
	}

	return errors;
}

/** Whether the submit control may act. The same rule the page renders its errors from, asked once. */
export function isSubmittable(draft: ContractCancellationDraft): boolean {
	return Object.keys(validateContractCancellation(draft)).length === 0;
}

/** What `POST /contracts/cancellation` accepts. Optional fields are ABSENT rather than empty strings. */
export interface ContractCancellationRequestBody {
	email: string;
	kind: ContractCancellationKind;
	name?: string;
	contractDetail?: string;
	reason?: string;
	requestedEffectiveDate?: string;
}

/**
 * The draft as the api's request body: trimmed, with every optional field the reader left empty omitted.
 *
 * <p>A reason is sent ONLY with an extraordinary cancellation. Somebody who types a reason, then switches
 * back to an ordinary cancellation, must not have that text filed as the content of an ordinary
 * declaration — § 312k Abs. 4 obliges us to confirm what was declared, and that would not be it.
 *
 * @throws never; call {@link isSubmittable} first — an invalid draft is the page's problem, not the wire's
 */
export function requestBodyOf(draft: ContractCancellationDraft): ContractCancellationRequestBody {
	const body: ContractCancellationRequestBody = {
		email: draft.email.trim(),
		// Narrowed by `isSubmittable`, which the page calls before this: an unanswered kind cannot be sent.
		kind: draft.kind === "" ? "ORDINARY" : draft.kind,
	};
	const name = draft.name.trim();
	if (name.length > 0) {
		body.name = name;
	}
	const contractDetail = draft.contractDetail.trim();
	if (contractDetail.length > 0) {
		body.contractDetail = contractDetail;
	}
	const reason = draft.reason.trim();
	if (draft.kind === "EXTRAORDINARY" && reason.length > 0) {
		body.reason = reason;
	}
	if (draft.requestedEffectiveDate !== "") {
		body.requestedEffectiveDate = draft.requestedEffectiveDate;
	}
	return body;
}

/**
 * The receipt moment as the confirmation view prints it: the api's own UTC instant, spelled exactly as the
 * confirmation EMAIL spells it for the same language, with the zone NAMED.
 *
 * <p><strong>UTC rather than the viewer's own clock, deliberately.</strong> Every other instant in this app
 * goes through `formatApiInstant`, which moves to the reader's zone because a deadline they cannot place is
 * worse than none. This one is different in kind: it is EVIDENCE, and the confirmation email — which is the
 * same receipt in the reader's inbox — states the instant in UTC through the api's own shared formatter. Two
 * documents that prove the same moment must not appear to disagree, so the page says what the email says.
 * The zone is named for the same reason: a time with no zone is ambiguous evidence months later.
 *
 * <p><strong>English is ISO-ordered, and that is the email's rule rather than a departure from it</strong>
 * (kitecorp/transferify-ui#2587). German and Romanian write the day first with stops; English keeps
 * `2026-09-12 14:30 UTC`, because a month-first `09/12/2026` is the one numeric spelling a reader months
 * later and in another country cannot read back unambiguously. The api's `EmailDates.receiptMoment` chooses
 * per language for the same reason, and `EmailDatesTest` pins the same three literals this file's test does.
 * An earlier version of this comment claimed the page "says what the email says" while rendering English as
 * `09/12/2026, 02:30 PM UTC` — the two documents genuinely disagreed for English readers only, which is
 * exactly the doubt a receipt exists to remove.
 *
 * @param receivedAt the api's ISO-8601 receipt instant; `null` when it is absent or unparseable, so the
 *   view can omit the sentence rather than print "Invalid DateTime" into somebody's proof
 */
export function formatCancellationReceipt(
	receivedAt: string | null | undefined,
	locale: Locale,
): string | null {
	const instant = parseApiInstant(receivedAt);
	if (instant === null) {
		return null;
	}
	if (locale === "en") {
		return formatIsoMinuteWithZone(instant);
	}
	return formatDateTimeWith(instant, locale, {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		timeZoneName: "short",
	});
}

/**
 * The desired end date as the confirmation view prints it back.
 *
 * <p>The date input hands us `YYYY-MM-DD`, and the proof page used to print that string unchanged — so a
 * German reader saw `2026-11-30` on screen while the confirmation email for the same declaration said
 * `30. November 2026`, and the page's own error copy for a bad date offers `31.12.2026` as the example.
 * Three spellings of one date across one flow is not a formatting preference: this page is the reader's
 * evidence, and evidence that does not match the email raises exactly the doubt it exists to remove.
 *
 * <p>Parsed as a CALENDAR date, not an instant: it carries no time and no zone, so turning it into one
 * would let a reader east or west of UTC see the day before or after the one they chose.
 *
 * @param iso the draft's `YYYY-MM-DD`, or `""` when the reader named no date
 * @returns the formatted date, or `null` when there is nothing to format or it does not parse — the view
 *   prints the statutory "earliest possible" wording instead, which is what an absent date means anyway
 */
export function formatRequestedEffectiveDate(iso: string, locale: Locale): string | null {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
	if (match === null) {
		return null;
	}
	const [, year, month, day] = match;
	const date = new Date(Number(year), Number(month) - 1, Number(day));
	if (Number.isNaN(date.getTime()) || date.getMonth() !== Number(month) - 1) {
		return null;
	}
	return new Intl.DateTimeFormat(locale, {day: "numeric", month: "long", year: "numeric"}).format(date);
}
